/**
 * BondSeriesResultsTable - Displays bond series results from candidate processing
 * Shows payment schedule with actual payment dates and bond payment details
 * Follows the builder pattern for consistent table behavior
 */

import { Box, Text, VStack } from '@chakra-ui/react';
import React from 'react';

import { type ColumnDefinition, ReusableTable } from '~/components/tables/ReusableTable';
import type { CashFlow } from '~/types';
import { formatCurrency } from '~/utils/formatting-utils';

interface BondSeriesResultsTableProps {
  bondSeriesId: string;
  bondSeriesName: string;
  cashFlows: CashFlow[];
  title?: string;
}

type BondPaymentRow = {
  paymentDate: string;
  bondSeriesName: string;
  principal: number;
  interestRate: number;
  newInterest: number;
  futureInterestObligation: number;
  totalPayment: number;
};

// Local percentage formatter for interest rates
const formatPercentage = (value: number): string => `${(value * 100).toFixed(2)}%`;

// Extract year from bond name (e.g., "2025 Bond" → "2025")
const extractBondYear = (bondName: string): string => {
  const yearMatch = bondName.match(/\d{4}/);
  return yearMatch ? yearMatch[0] : bondName;
};

export const BondSeriesResultsTable: React.FC<BondSeriesResultsTableProps> = ({
  bondSeriesId,
  bondSeriesName,
  cashFlows,
  title,
}) => {
  const displayName = title || extractBondYear(bondSeriesName);
  const tableData: BondPaymentRow[] = React.useMemo(() => {
    return cashFlows
      .filter((cf) => cf.bondPayments.some((bp) => bp.bondSeriesId === bondSeriesId))
      .flatMap((cashFlow) => {
        const bondPayments = cashFlow.bondPayments.filter((bp) => bp.bondSeriesId === bondSeriesId);

        return bondPayments.map((bondPayment) => ({
          paymentDate: cashFlow.paymentDate,
          bondSeriesName: bondPayment.bondSeriesName,
          principal: bondPayment.principal,
          interestRate: bondPayment.interestRate,
          newInterest: bondPayment.newInterest,
          futureInterestObligation: bondPayment.futureInterestObligation || 0,
          totalPayment: bondPayment.totalPayment,
        }));
      });
  }, [cashFlows, bondSeriesId]);

  const columns: ColumnDefinition<BondPaymentRow>[] = [
    {
      key: 'paymentDate',
      header: 'Payment Date',
      accessor: (row) => row.paymentDate,
      headerTextAlign: 'left',
      cellTextAlign: 'left',
    },
    {
      key: 'principal',
      header: 'Principal',
      accessor: (row) => formatCurrency(row.principal),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
      disableSort: true,
    },
    {
      key: 'interestRate',
      header: 'Interest Rate',
      accessor: (row) => formatPercentage(row.interestRate),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
      disableSort: true,
    },
    {
      key: 'newInterest',
      header: 'Interest',
      accessor: (row) => formatCurrency(row.newInterest),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
      disableSort: true,
    },
    {
      key: 'futureInterestObligation',
      header: 'Interest Obligation',
      accessor: (row) => formatCurrency(row.futureInterestObligation),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
      disableSort: true,
    },
    {
      key: 'totalPayment',
      header: 'Total Payment',
      accessor: (row) => formatCurrency(row.totalPayment),
      headerTextAlign: 'right',
      cellTextAlign: 'right',
      disableSort: true,
    },
  ];

  // Calculate totals for footer row
  const footerRowData = React.useMemo(() => {
    if (tableData.length === 0) return null;

    const totalPrincipal = tableData.reduce((sum, row) => sum + row.principal, 0);
    const totalInterest = tableData.reduce((sum, row) => sum + row.newInterest, 0);
    const totalFutureInterest = tableData.reduce(
      (sum, row) => sum + row.futureInterestObligation,
      0
    );
    const totalPayment = tableData.reduce((sum, row) => sum + row.totalPayment, 0);

    const footerData: Partial<BondPaymentRow> = {
      paymentDate: 'TOTALS',
      bondSeriesName: '',
      principal: totalPrincipal,
      interestRate: 0, // Not applicable for totals
      newInterest: totalInterest,
      futureInterestObligation: totalFutureInterest,
      totalPayment: totalPayment,
    };

    return footerData;
  }, [tableData]);

  if (tableData.length === 0) {
    return (
      <Box p={4} bg="yellow.50" borderRadius="md" borderLeft="4px solid" borderColor="yellow.500">
        <Text color="yellow.800" fontSize="sm">
          No payment data available for {displayName}. Ensure the bond series has been processed.
        </Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" gap={4}>
      {title && (
        <Text fontSize="md" fontWeight="semibold">
          {title}
        </Text>
      )}

      <Box overflowX="auto">
        <ReusableTable<BondPaymentRow>
          data={tableData}
          columns={columns}
          footerRow={footerRowData}
        />
      </Box>
    </VStack>
  );
};

export default BondSeriesResultsTable;
